import { useCallback, useEffect, useRef, useState } from 'react'
import {
  InstallationErrorPayload,
  InstallationRequestAccepted,
  InstallationRequestStatus,
  InstallationRequestUpdatedEvent,
  TERMINAL_INSTALLATION_REQUEST_STATUSES,
} from 'common/types/kraken-core/Installation'
import { getInstallationRequest } from 'services/kraken-core/installations/installations.service'
import {
  getStoredInstallationRequest,
  INSTALLATION_REQUEST_MAX_AGE_MS,
  isInstallationRequestExpired,
  removeStoredInstallationRequest,
  setStoredInstallationRequest,
  StoredInstallationRequest,
} from '../installationRequestStorage'

const INSTALLATION_REQUEST_UPDATED_EVENT = 'installation_request.updated'

export interface UseInstallationRequestState {
  status: InstallationRequestStatus | null
  attempts: number
  error: InstallationErrorPayload | null
  installationId: string | null
  isTracking: boolean
  hasTimedOut: boolean
}

export interface UseInstallationRequestResult extends UseInstallationRequestState {
  /** Starts observing a just-dispatched apply/install through to completion - call
   * this with the `202` response body right after the dispatch POST resolves. */
  trackInstallationRequestProgress: (accepted: InstallationRequestAccepted) => void
}

export type TerminalStatusMessages = Partial<Record<InstallationRequestStatus, string>>

/**
 * Maps a terminal (non-`succeeded`) InstallationRequest status to the error payload a wizard
 * should show. A status with an `overrides` entry (e.g. a rollback outcome) gets that message
 * with the request's own diagnostics/raw_output attached; any other status falls back to the
 * request's error, or `fallbackMessage` if the request carries none.
 */
export function resolveTerminalStatusError(
  status: InstallationRequestStatus,
  error: InstallationErrorPayload | null,
  overrides: TerminalStatusMessages,
  fallbackMessage: string
): InstallationErrorPayload {
  const overrideMessage = overrides[status]
  if (!overrideMessage) return error || { message: fallbackMessage }
  return {
    message: overrideMessage,
    diagnostics: error?.diagnostics,
    raw_output: error?.raw_output,
  }
}

const INITIAL_STATE: UseInstallationRequestState = {
  status: null,
  attempts: 0,
  error: null,
  installationId: null,
  isTracking: false,
  hasTimedOut: false,
}

type UpdateEvent = Pick<
  InstallationRequestUpdatedEvent,
  'status' | 'attempts' | 'error' | 'installation_id'
>

/**
 * Tracks a self-service apply/install `InstallationRequest` over Pusher (same pattern as
 * `useArtifactGeneration`): subscribe, update on every `installation_request.updated`
 * event, and recover an in-flight request after remount via a single GET.
 * `subjectId` keys local storage for that recovery (installation or module id).
 */
export function useInstallationRequest(
  subjectId: string | null | undefined
): UseInstallationRequestResult {
  const [state, setState] = useState<UseInstallationRequestState>(INITIAL_STATE)

  const cleanupRef = useRef<(() => void) | null>(null)
  const hasUnmountedRef = useRef(false)
  const recoveredSubjectIdRef = useRef<string | null>(null)

  const activeRequestIdRef = useRef<string | null>(null)
  // True after the first live push for this subscription; late reconcile GETs must not apply.
  const receivedLiveEventRef = useRef(false)

  const finishTracking = useCallback(() => {
    activeRequestIdRef.current = null
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }
    if (!hasUnmountedRef.current) {
      setState(previous => ({ ...previous, isTracking: false }))
    }
  }, [])

  const updateUiFromEvent = useCallback(
    (event: UpdateEvent) => {
      if (hasUnmountedRef.current) return
      setState(previous => ({
        ...previous,
        status: event.status,
        attempts: event.attempts,
        error: event.error,
        installationId: event.installation_id,
      }))
      if (TERMINAL_INSTALLATION_REQUEST_STATUSES.includes(event.status)) {
        if (subjectId) removeStoredInstallationRequest(subjectId)
        finishTracking()
      }
    },
    [subjectId, finishTracking]
  )

  const subscribe = useCallback(
    (channel: string, remainingMs: number) => {
      receivedLiveEventRef.current = false

      const timeout = setTimeout(() => {
        finishTracking()
        setState(previous => ({ ...previous, hasTimedOut: true }))
      }, Math.max(remainingMs, 0))

      let unbindLiveEvents: (() => void) | null = null

      try {
        // Dynamic require mirrors the existing pattern used by useArtifactGeneration.
        const { getPusherClient } = require('common/pusher/pusherClient')
        const pusher = getPusherClient()
        const pusherChannel = pusher.subscribe(channel)

        const handleUpdated = (event: InstallationRequestUpdatedEvent) => {
          receivedLiveEventRef.current = true
          updateUiFromEvent(event)
        }
        pusherChannel.bind(INSTALLATION_REQUEST_UPDATED_EVENT, handleUpdated)

        unbindLiveEvents = () => {
          pusherChannel.unbind?.(INSTALLATION_REQUEST_UPDATED_EVENT, handleUpdated)
          pusher.unsubscribe(channel)
        }
      } catch (error) {
        // Pusher isn't configured in every environment (e.g. local dev without app keys) -
        // fall back to the reconcile GET and the timeout above instead of losing tracking.
        console.warn('useInstallationRequest: failed to subscribe to Pusher', error)
      }

      cleanupRef.current = () => {
        clearTimeout(timeout)
        unbindLiveEvents?.()
      }
    },
    [updateUiFromEvent, finishTracking]
  )

  // One-off GET after subscribe to close the race where a fast-failing Celery task
  // finishes (and notifies) before the browser subscription exists. Missing that only
  // terminal event would leave the UI spinning until timeout.
  const refreshInstallationRequestProgress = useCallback(
    async (requestId: string) => {
      const { response, error } = await getInstallationRequest(requestId)
      if (error || !response?.data) return
      if (activeRequestIdRef.current !== requestId) return // superseded or already finished
      // live push already showed a fresher (or equal) state in the UI
      if (receivedLiveEventRef.current) return

      const detail = response.data
      updateUiFromEvent({
        status: detail.status,
        attempts: detail.attempts,
        error: detail.error,
        installation_id: detail.installation_id,
      })
    },
    [updateUiFromEvent]
  )

  const trackInstallationRequestProgress = useCallback(
    (accepted: InstallationRequestAccepted) => {
      if (!subjectId) return
      const stored: StoredInstallationRequest = {
        id: accepted.installation_request_id,
        subject_id: subjectId,
        status: accepted.status,
        channel: accepted.channel,
        created_at: new Date().toISOString(),
      }
      setStoredInstallationRequest(stored)
      setState({
        status: accepted.status,
        attempts: 0,
        error: null,
        installationId: null,
        isTracking: true,
        hasTimedOut: false,
      })
      activeRequestIdRef.current = accepted.installation_request_id
      subscribe(accepted.channel, INSTALLATION_REQUEST_MAX_AGE_MS)
      refreshInstallationRequestProgress(accepted.installation_request_id)
    },
    [subjectId, subscribe, refreshInstallationRequestProgress]
  )

  useEffect(() => {
    return () => {
      hasUnmountedRef.current = true
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!subjectId) return
    if (recoveredSubjectIdRef.current === subjectId) return
    recoveredSubjectIdRef.current = subjectId

    const stored = getStoredInstallationRequest(subjectId)
    if (!stored) return

    let cancelled = false

    const recoverInstallationRequest = async () => {
      if (isInstallationRequestExpired(stored)) {
        removeStoredInstallationRequest(subjectId)
        return
      }

      const { response, error } = await getInstallationRequest(stored.id)
      if (cancelled) return

      if (error || !response?.data) {
        removeStoredInstallationRequest(subjectId)
        return
      }

      const detail = response.data

      if (TERMINAL_INSTALLATION_REQUEST_STATUSES.includes(detail.status)) {
        removeStoredInstallationRequest(subjectId)
        updateUiFromEvent({
          status: detail.status,
          attempts: detail.attempts,
          error: detail.error,
          installation_id: detail.installation_id,
        })
        return
      }

      const createdAtMs = new Date(stored.created_at).getTime()
      const remainingMs = createdAtMs + INSTALLATION_REQUEST_MAX_AGE_MS - Date.now()

      setState({
        status: detail.status,
        attempts: detail.attempts,
        error: detail.error,
        installationId: detail.installation_id,
        isTracking: true,
        hasTimedOut: false,
      })
      activeRequestIdRef.current = stored.id
      subscribe(detail.channel, remainingMs)
    }

    recoverInstallationRequest()

    return () => {
      cancelled = true
    }
  }, [subjectId, subscribe, updateUiFromEvent])

  return { ...state, trackInstallationRequestProgress }
}
